import { HeroCardBody } from './HeroCardBody';
import { HeroCardCtas } from './HeroCardCtas';
import { HeroCardPanel, type HeroCardAlign } from './HeroCardPanel';
import { resolveHeroCardText } from './resolveHeroCard';
import type { HeroCardTextConfig, HeroCardRootProps } from './types';
import { Text } from '@/lib/render/Text';

export interface HeroCardTextProps extends HeroCardRootProps {
  config: HeroCardTextConfig;
  /** `left` serves `widgets.hero-card-text`, `center` the `-centered` schema. */
  align?: HeroCardAlign;
}

// Typography is fixed per card type, never CMS-configured (SH-312 Q3/Q4): the
// Editor's choice of component is what selects it. The centered card renders a
// smaller heading than the left-aligned one (O1).
const TITLE_CLASS: Record<HeroCardAlign, string> = {
  left: 'text-xl leading-tight font-bold tracking-tight lg:text-2xl',
  center: 'text-lg leading-tight font-bold tracking-tight lg:text-xl',
};

/**
 * Serves both text card schemas — `widgets.hero-card-text` (subject and contact
 * pages) and `widgets.hero-card-text-centered` (federal-state and expansion
 * pages). The two have identical field sets; the schema split exists only so
 * the component choice carries the typography.
 *
 * One rich-text body covers both a checkmark benefits list and a plain address
 * block, because `.benefits-checkmarks` only styles `ul > li`.
 */
export function HeroCardText({
  config,
  align = 'left',
  ...rest
}: HeroCardTextProps) {
  const resolved = resolveHeroCardText(config);

  return (
    <HeroCardPanel align={align} {...rest}>
      {resolved.title && (
        <Text as="h2" field="title" className={TITLE_CLASS[align]}>
          {resolved.title}
        </Text>
      )}
      <HeroCardBody nodes={resolved.body} />
      <HeroCardCtas
        actions={resolved.ctas}
        fallbackPhone={resolved.phone || undefined}
        fallbackMatelsoPhoneLabel={resolved.matelsoPhoneLabel || undefined}
      />
    </HeroCardPanel>
  );
}
